import * as React from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { ConsultationBand, PublicHero, PublicShell } from "@/components/PublicShell"
import { PublicMeta } from "@/components/public/PublicMeta"
import { WineCard, type WineCardData } from "@/components/public/WineCard"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"
import "@/styles/public-finder.css"

const TITLE = "Find Your Wine"
const DESCRIPTION =
  "Three questions — your budget per bottle, the style of wine and how you like it to taste — and we show you the South African wines from our range that fit."

type BudgetBand = { key: string; label: string; min_pence: number; max_pence: number | null }
type TypeOption = { key: string; label: string; explainer: string | null }
type TasteOption = { key: string; label: string }

type Criteria = {
  band: string | null
  budget_pence: number | null
  types: string[]
  taste: string | null
  budget_invalid: boolean
}


type Results = {
  summary: string
  mode: "matches" | "nearest_above" | "unpriced" | "empty"
  note: string | null
  footnote: string | null
  wines: WineCardData[]
}

type FinderProps = PageProps<{
  budget_bands: BudgetBand[]
  type_options: TypeOption[]
  taste_options: TasteOption[]
  criteria: Criteria
  results: Results | null
}>

type State = Pick<Criteria, "band" | "budget_pence" | "types" | "taste">

const RESULTS_ID = "finder-results"

// Pence → the pounds text shown in the custom amount field ("15", "14.50").
function poundsText(pence: number | null) {
  if (pence === null) return ""
  return pence % 100 === 0 ? String(pence / 100) : (pence / 100).toFixed(2)
}

// Mirrors WineFinder.pence_from_pounds on the server.
function penceFromPounds(raw: string): number | null {
  const text = raw.trim().replace(/^£/, "").trim()
  if (!/^\d{1,5}(\.\d{1,2})?$/.test(text)) return null
  const pence = Math.round(Number.parseFloat(text) * 100)
  return pence > 0 ? pence : null
}

// The query params for a finder state (?budget=1450&types[]=red&taste=dry).
function queryFor(state: State) {
  const query: Record<string, string | string[]> = {}
  if (state.band) query.band = state.band
  else if (state.budget_pence !== null) query.budget = String(state.budget_pence)
  if (state.types.length > 0) query.types = state.types
  if (state.taste) query.taste = state.taste
  return query
}

// A real URL for each choice, so every step works as a plain link before
// hydration (and can be opened in a new tab).
function hrefFor(state: State) {
  const search = new URLSearchParams()
  const query = queryFor(state)
  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) value.forEach((item) => search.append(`${key}[]`, item))
    else search.append(key, value)
  }
  const text = search.toString()
  return text ? `/finder?${text}` : "/finder"
}

function hasBudget(state: State) {
  return state.band !== null || state.budget_pence !== null
}

function isComplete(state: State) {
  return hasBudget(state) && state.types.length > 0 && state.taste !== null
}

export default function Finder() {
  const page = usePage<FinderProps>()
  const { budget_bands, type_options, taste_options, criteria, results, selection } = page.props

  const [state, setState] = React.useState<State>({
    band: criteria.band,
    budget_pence: criteria.budget_pence,
    types: criteria.types,
    taste: criteria.taste,
  })
  const [loading, setLoading] = React.useState(false)

  // The server echoes the criteria on every visit (including history
  // navigation), so keep the local, optimistic state in step with it.
  React.useEffect(() => {
    setState({
      band: criteria.band,
      budget_pence: criteria.budget_pence,
      types: criteria.types,
      taste: criteria.taste,
    })
  }, [criteria])

  const visit = (next: State) => {
    const wasComplete = isComplete(state)
    setState(next)
    router.get("/finder", queryFor(next), {
      preserveState: true,
      preserveScroll: true,
      only: ["results", "criteria"],
      onStart: () => setLoading(true),
      onFinish: () => setLoading(false),
      onSuccess: () => {
        // The first time all three steps are answered, bring the results in.
        if (!wasComplete && isComplete(next)) {
          document.getElementById(RESULTS_ID)?.scrollIntoView({ behavior: "smooth", block: "start" })
        }
      },
    })
  }

  const choose = (next: State) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
    event.preventDefault()
    visit(next)
  }

  const mccExplainer = type_options.find((option) => option.key === "mcc")?.explainer ?? ""

  const showResults = isComplete(state) && results !== null

  return (
    <PublicShell>
      <PublicMeta title={TITLE} description={DESCRIPTION} />

      <PublicHero
        eyebrow="The wine finder"
        title={
          <>
            Find your wine, <span className="soft">in three questions.</span>
          </>
        }
        lede="Tell us your budget per bottle, the style of wine and how you like it to taste. We will show you the wines from the range that fit."
      />

      <section className="band band-charcoal finder-band" aria-labelledby="finder-steps-title">
        <div className="shell">
          <h2 id="finder-steps-title" className="sr-only">
            Your three questions
          </h2>
          <ol className="finder-steps">
            <BudgetStep
              bands={budget_bands}
              state={state}
              budgetInvalid={criteria.budget_invalid}
              choose={choose}
              visit={visit}
            />
            <TypeStep options={type_options} state={state} choose={choose} />
            <TasteStep options={taste_options} state={state} choose={choose} />
          </ol>
        </div>
      </section>

      {showResults && (
        <section
          id={RESULTS_ID}
          className={cn("band band-black finder-results", loading && "is-loading")}
          aria-labelledby="finder-results-title"
          aria-busy={loading}
        >
          <div className="shell">
            <div className="finder-results-head">
              <div className="eyebrow">Your wines</div>
              <h2 id="finder-results-title">{results.summary}</h2>
              <p className="finder-count" aria-live="polite">
                {resultsCountText(results)}
              </p>
              {results.note && <p className="finder-note">{results.note}</p>}
            </div>

            {results.wines.length > 0 ? (
              <ul className="finder-grid">
                {results.wines.map((wine) => (
                  <li key={wine.id}>
                    <WineCard wine={wine} mccExplainer={mccExplainer} returnTo={page.url} />
                  </li>
                ))}
              </ul>
            ) : (
              <div className="finder-empty">
                <Button asChild variant="ghost-hairline" size="sm">
                  <Link href="/wines">Browse the full range</Link>
                </Button>
                <Button asChild variant="gold" size="sm">
                  <Link href="/contact">Request a consultation</Link>
                </Button>
              </div>
            )}

            {results.footnote && (
              <p className="finder-footnote">
                {results.footnote}. Choose “No preference” to see them all.
              </p>
            )}

            {selection.bottle_count > 0 && (
              <div className="finder-exit">
                <p className="finder-exit-count">
                  Your selection · {selection.bottle_count}{" "}
                  {selection.bottle_count === 1 ? "bottle" : "bottles"}
                  {selection.minimum_met ? "" : ` · minimum ${selection.minimum}`}
                </p>
                <div className="finder-exit-actions">
                  <Button asChild variant="gold" size="lg">
                    <Link href="/studio">Now design your label</Link>
                  </Button>
                  <Button asChild variant="ghost-hairline" size="lg">
                    <Link href="/order">Review your selection</Link>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <ConsultationBand />
    </PublicShell>
  )
}

function resultsCountText(results: Results) {
  const n = results.wines.length
  const wines = `${n} ${n === 1 ? "wine" : "wines"}`
  switch (results.mode) {
    case "matches":
      return `${wines} within your budget, from the lowest price`
    case "nearest_above":
      return `${wines} just above your budget`
    case "unpriced":
      return `${wines} awaiting pricing`
    default:
      return "No wines to show yet"
  }
}

type ChooseFn = (next: State) => (event: React.MouseEvent<HTMLAnchorElement>) => void

function StepHead({ numeral, title, hint }: { numeral: string; title: string; hint?: string }) {
  return (
    <div className="finder-step-head">
      <span className="finder-numeral" aria-hidden="true">
        {numeral}
      </span>
      <div>
        <h3>{title}</h3>
        {hint && <p className="finder-hint">{hint}</p>}
      </div>
    </div>
  )
}

function Chip({
  href,
  selected,
  onClick,
  children,
}: {
  href: string
  selected: boolean
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void
  children: React.ReactNode
}) {
  return (
    <a
      href={href}
      onClick={onClick}
      className={cn("finder-chip", selected && "is-selected")}
      aria-current={selected ? "true" : undefined}
    >
      <span className="finder-chip-mark" aria-hidden="true" />
      {children}
    </a>
  )
}

function BudgetStep({
  bands,
  state,
  budgetInvalid,
  choose,
  visit,
}: {
  bands: BudgetBand[]
  state: State
  budgetInvalid: boolean
  choose: ChooseFn
  visit: (next: State) => void
}) {
  const [amount, setAmount] = React.useState(poundsText(state.budget_pence))

  React.useEffect(() => {
    if (state.budget_pence !== null) setAmount(poundsText(state.budget_pence))
    else if (state.band !== null) setAmount("")
  }, [state.budget_pence, state.band])
  const [error, setError] = React.useState<string | null>(
    budgetInvalid ? "Enter an amount in pounds, like 15 or 14.50." : null,
  )

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const pence = penceFromPounds(amount)
    if (pence === null) {
      setError("Enter an amount in pounds, like 15 or 14.50.")
      return
    }
    setError(null)
    visit({ ...state, band: null, budget_pence: pence })
  }

  return (
    <li className="finder-step">
      <StepHead numeral="I" title="Your budget per bottle" hint="Choose a range, or set your own ceiling." />
      <div className="finder-step-body">
        <ul className="finder-chips" aria-label="Budget per bottle">
          {bands.map((band) => {
            const next = { ...state, band: band.key, budget_pence: null }
            return (
              <li key={band.key}>
                <Chip href={hrefFor(next)} selected={state.band === band.key} onClick={choose(next)}>
                  {band.label}
                </Chip>
              </li>
            )
          })}
        </ul>

        <form method="get" action="/finder" className="finder-custom" onSubmit={submit} noValidate>
          {state.types.map((type) => (
            <input key={type} type="hidden" name="types[]" value={type} />
          ))}
          {state.taste && <input type="hidden" name="taste" value={state.taste} />}
          <label htmlFor="finder-budget" className="field-label">Or your own amount</label>
          <div className="finder-custom-row">
            <div className={cn("finder-pound", state.budget_pence !== null && "is-selected")}>
              <span aria-hidden="true">£</span>
              <input
                id="finder-budget"
                name="budget_gbp"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="15"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                className="form-control form-control-cellar"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? "finder-budget-error" : "finder-budget-hint"}
              />
            </div>
            <Button type="submit" variant="ghost-hairline" size="sm">
              Set budget
            </Button>
          </div>
          {error ? (
            <p id="finder-budget-error" className="field-error">
              {error}
            </p>
          ) : (
            <p id="finder-budget-hint" className="field-hint">
              Per bottle, in pounds. We show wines up to this price.
            </p>
          )}
        </form>
      </div>
    </li>
  )
}

function TypeStep({ options, state, choose }: { options: TypeOption[]; state: State; choose: ChooseFn }) {
  if (!hasBudget(state)) {
    return <LockedStep numeral="II" title="The wine" note="After your budget" />
  }

  return (
    <li className="finder-step">
      <StepHead numeral="II" title="The wine" hint="Choose as many as you like — most orders mix them." />
      <div className="finder-step-body">
        <ul className="finder-chips finder-chips-types" aria-label="Wine type">
          {options.map((option) => {
            const selected = state.types.includes(option.key)
            const types = selected
              ? state.types.filter((type) => type !== option.key)
              : [...state.types, option.key]
            const next = { ...state, types }
            return (
              <li key={option.key} className="finder-type">
                <Chip href={hrefFor(next)} selected={selected} onClick={choose(next)}>
                  {option.label}
                </Chip>
                {option.explainer && <span className="finder-explainer">{option.explainer}</span>}
              </li>
            )
          })}
        </ul>
      </div>
    </li>
  )
}

function TasteStep({ options, state, choose }: { options: TasteOption[]; state: State; choose: ChooseFn }) {
  if (!hasBudget(state) || state.types.length === 0) {
    return <LockedStep numeral="III" title="The taste" note="After the wine" />
  }

  return (
    <li className="finder-step">
      <StepHead numeral="III" title="The taste" hint="How do your guests like their wine?" />
      <div className="finder-step-body">
        <ul className="finder-chips" aria-label="Taste">
          {options.map((option) => {
            const next = { ...state, taste: option.key }
            return (
              <li key={option.key}>
                <Chip href={hrefFor(next)} selected={state.taste === option.key} onClick={choose(next)}>
                  {option.label}
                </Chip>
              </li>
            )
          })}
        </ul>
      </div>
    </li>
  )
}

function LockedStep({ numeral, title, note }: { numeral: string; title: string; note: string }) {
  return (
    <li className="finder-step is-locked">
      <StepHead numeral={numeral} title={title} hint={note} />
    </li>
  )
}
